import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { withRouter, Link, useParams } from "react-router-dom";
import {
    Container,
    Button,
    ButtonToolbar,
    Row,
    Col,
    FormGroup,
    Label,
    Alert,
    Card,
    CardBody
} from "reactstrap";
import { Form, Field } from "react-final-form";
import axios from "axios";
import { sendFlashMessage } from "@/shared/components/flash";
import { getProgramAction } from "@/redux/actions/programActions";
import renderToggleButtonField from "@/shared/components/form/ToggleButton";
import ProgramBreadcrumb from '@/shared/components/ProgramBreadcrumb'

const ProgramEmailSettings = ({ program, organization, auth, dispatch }) => {
    const { programId } = useParams();
    const [loading, setLoading] = useState(true);
    const [emailSettings, setEmailSettings] = useState(null);
    const [inheritanceMap, setInheritanceMap] = useState({});

    const DEFAULT_EMAIL_SETTINGS = {
        enable_activation_emails: true,
        enable_activation_reminders: true,
        enable_award_emails: true,
        enable_general_emails: true,
        enable_password_reset_emails: true,
        enable_two_factor_emails: true,
        reminder_schedule_type: "FIXED",
        reminder_recurring_days: 7
    };

    useEffect(() => {
        if (organization?.id && programId) {
            setLoading(true);
            axios.get(`/organization/${organization.id}/program/${programId}/email-settings`)
                .then(response => {
                    const settings = response.data.settings || DEFAULT_EMAIL_SETTINGS;
                    const inherited = response.data.inheritedDisabled || {};
                    
                    // Force settings to false if inherited as disabled so toggles appear "off"
                    const forcedSettings = { ...settings };
                    Object.keys(inherited).forEach(fieldName => {
                        if (inherited[fieldName]) {
                            forcedSettings[fieldName] = false;
                        }
                    });

                    setEmailSettings(forcedSettings);
                    setInheritanceMap(inherited);
                    setLoading(false);
                })
                .catch(error => {
                    dispatch(sendFlashMessage("Failed to load email configurations", "alert-danger", "top"));
                    setLoading(false);
                });
        }
    }, [programId, organization?.id]);

    useEffect(() => {
        if (programId && organization?.id && (!program || program.id !== programId)) {
            dispatch(getProgramAction(organization.id, programId, { 'hierarchy': true }))
        }
    }, [programId, organization, dispatch])

    const onSubmitForm = async (values) => {
        setLoading(true);
        
        // Ensure inherited disabled settings are explicitly saved as false
        const submittableValues = { ...values };
        Object.keys(inheritanceMap).forEach(fieldName => {
            if (inheritanceMap[fieldName]) {
                submittableValues[fieldName] = false;
            }
        });

        try {
            const response = await axios.put(
                `/organization/${organization.id}/program/${programId}/email-settings`, 
                submittableValues
            );
            if (response.status === 200) {
                setEmailSettings(response.data.settings);
                dispatch(sendFlashMessage("Email configurations updated successfully", "alert-success", "top"));
                dispatch(getProgramAction(organization.id, programId));
            }
        } catch (error) {
            dispatch(sendFlashMessage("Could not update email configuration settings", "alert-danger", "top"));
            console.error(`API Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const isFieldDisabled = (fieldName, isCritical = false) => {
        if (inheritanceMap[fieldName]) return true;
        if (isCritical && !auth?.isSuperAdmin) return true;
        return false;
    };

    const renderInheritedMessage = (fieldName) => {
        if (inheritanceMap[fieldName]) {
            return <small className="text-danger d-block mt-1">Disabled by parent program</small>;
        }
        return null;
    };

    if (!program || !organization) return 'Loading...'

    return (
        <Container className="program-email-settings">
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Email Control Configurations</h3>
                    <h3 className="page-subhead subhead">
                        <Link className="" to="/">Home</Link>{" / "}
                        <Link className="" to="/program">Programs</Link>
                        <ProgramBreadcrumb program={program} />{" / "}
                        Email Control Configurations
                    </h3>
                </Col>
            </Row>

            <Form
                onSubmit={onSubmitForm}
                initialValues={{ ...DEFAULT_EMAIL_SETTINGS, ...emailSettings }}
                keepDirtyOnReinitialize
            >
                {({ handleSubmit, values }) => (
                    <form onSubmit={handleSubmit} className="form d-block">
                        <Row>
                            <Col md={12}>
                                <Card>
                                    <CardBody>
                                        <div className="card__title">
                                            <Row>
                                                <Col md="3">
                                                    <h5 className="bold-text mb-3">Email Settings</h5>
                                                </Col>
                                                <Col md="9" className="text-right">
                                                    <ButtonToolbar className="flex justify-content-right w100">
                                                        <Link to={`/program/view/${programId}`} className="btn btn-outline-secondary mr-3">
                                                            Back to Program
                                                        </Link>
                                                        <Button type="submit" disabled={loading} color="primary">
                                                            {loading ? 'Saving...' : 'Save Changes'}
                                                        </Button>
                                                    </ButtonToolbar>
                                                </Col>
                                            </Row>
                                        </div>

                                        {loading && !emailSettings ? (
                                            <div className="text-center py-4">Loading Email Configurations...</div>
                                        ) : (
                                            <fieldset className="p-3 email-settings-fieldset">
                                                <div className="settings-section mb-4">
                                                    <h4 className="mb-3">Email Categories</h4>  
                                                    
                                                    <Row>
                                                        <Col md="6" sm="12">
                                                            <FormGroup className="mb-4 align-items-start d-flex flex-column">
                                                                <div className="d-flex align-items-center w-100">
                                                                    <Label className="bold-text mb-0 mr-3" style={{minWidth: '200px'}}>Activation Emails</Label>
                                                                    <Field
                                                                        name="enable_activation_emails"
                                                                        component={renderToggleButtonField}
                                                                        disabled={isFieldDisabled("enable_activation_emails")}
                                                                    />
                                                                </div>
                                                                {renderInheritedMessage("enable_activation_emails")}
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    <Row>
                                                        <Col md="6" sm="12">
                                                            <FormGroup className="mb-4 align-items-start d-flex flex-column">
                                                                <div className="d-flex align-items-center w-100">
                                                                    <Label className="bold-text mb-0 mr-3" style={{minWidth: '200px'}}>Award Emails</Label>
                                                                    <Field
                                                                        name="enable_award_emails"
                                                                        component={renderToggleButtonField}
                                                                        disabled={isFieldDisabled("enable_award_emails")}
                                                                    />
                                                                </div>
                                                                {renderInheritedMessage("enable_award_emails")}
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    <Row>
                                                        <Col md="6" sm="12">
                                                            <FormGroup className="mb-4 align-items-start d-flex flex-column">
                                                                <div className="d-flex align-items-center w-100">
                                                                    <Label className="bold-text mb-0 mr-3" style={{minWidth: '200px'}}>General Emails</Label>
                                                                    <Field
                                                                        name="enable_general_emails"
                                                                        component={renderToggleButtonField}
                                                                        disabled={isFieldDisabled("enable_general_emails")}
                                                                    />
                                                                </div>
                                                                {renderInheritedMessage("enable_general_emails")}
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </div>

                                                <div className="settings-section mb-4">
                                                    <h4 className="mb-3 mt-4">Critical System Emails</h4>
                                                    
                                                    <Row>
                                                        <Col md="6" sm="12">
                                                            <FormGroup className="mb-4 align-items-start d-flex flex-column">
                                                                <div className="d-flex align-items-center w-100">
                                                                    <div style={{minWidth: '200px'}} className="mr-3">
                                                                        <Label className="bold-text mb-0">Password Reset Emails</Label>
                                                                        {!auth?.isSuperAdmin && !inheritanceMap["enable_password_reset_emails"] && (
                                                                            <small className="text-muted d-block mt-1">Requires Super Admin access to change</small>
                                                                        )}
                                                                    </div>
                                                                    <Field
                                                                        name="enable_password_reset_emails"
                                                                        component={renderToggleButtonField}
                                                                        disabled={isFieldDisabled("enable_password_reset_emails", true)}
                                                                    />
                                                                </div>
                                                                {renderInheritedMessage("enable_password_reset_emails")}
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    <Row>
                                                        <Col md="6" sm="12">
                                                            <FormGroup className="mb-4 align-items-start d-flex flex-column">
                                                                <div className="d-flex align-items-center w-100">
                                                                    <div style={{minWidth: '200px'}} className="mr-3">
                                                                        <Label className="bold-text mb-0">2FA Security Emails</Label>
                                                                        {!auth?.isSuperAdmin && !inheritanceMap["enable_two_factor_emails"] && (
                                                                            <small className="text-muted d-block mt-1">Requires Super Admin access to change</small>
                                                                        )}
                                                                    </div>
                                                                    <Field
                                                                        name="enable_two_factor_emails"
                                                                        component={renderToggleButtonField}
                                                                        disabled={isFieldDisabled("enable_two_factor_emails", true)}
                                                                    />
                                                                </div>
                                                                {renderInheritedMessage("enable_two_factor_emails")}
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </div>

                                                <div className="settings-section mb-4">
                                                    <h4 className="mb-3 mt-4">Activation Reminders</h4>
                                                    
                                                    <Row>
                                                        <Col md="6" sm="12">
                                                            <FormGroup className="mb-4 align-items-start d-flex flex-column">
                                                                <div className="d-flex align-items-center w-100">
                                                                    <Label className="bold-text mb-0 mr-3" style={{minWidth: '200px'}}>Enable Reminders</Label>
                                                                    <Field
                                                                        name="enable_activation_reminders"
                                                                        component={renderToggleButtonField}
                                                                        disabled={isFieldDisabled("enable_activation_reminders")}
                                                                    />
                                                                </div>
                                                                {renderInheritedMessage("enable_activation_reminders")}
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    {values.enable_activation_reminders && !inheritanceMap["enable_activation_reminders"] && (
                                                        <div className="mt-3 p-4 bg-light border rounded">
                                                            <label className="bold-text mb-3 d-block">Reminder Scheduling Options</label>
                                                            
                                                            <Row className="email-reminder-cards">
                                                                <Col md="6" className="mb-3">
                                                                    <label className={`reminder-card-label ${values.reminder_schedule_type === 'FIXED' ? 'active' : ''}`}>
                                                                        <div className="radio-input-wrapper">
                                                                            <Field
                                                                                name="reminder_schedule_type"
                                                                                component="input"
                                                                                type="radio"
                                                                                value="FIXED"
                                                                                className="custom-card-radio"
                                                                            />
                                                                            <span className="custom-radio-circle"></span>
                                                                        </div>
                                                                        
                                                                        <div className="icon-wrapper">
                                                                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                                                                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                                                                            </svg>
                                                                        </div>

                                                                        <div className="text-wrapper">
                                                                            <span className="card-title">Fixed Schedule</span>
                                                                            <span className="card-description">Send systematically at 7, 14, 21, and 28 days (Max 4 reminders).</span>
                                                                        </div>
                                                                    </label>
                                                                </Col>

                                                                <Col md="6" className="mb-3">
                                                                    <label className={`reminder-card-label ${values.reminder_schedule_type === 'RECURRING' ? 'active' : ''}`}>
                                                                        <div className="radio-input-wrapper">
                                                                            <Field
                                                                                name="reminder_schedule_type"
                                                                                component="input"
                                                                                type="radio"
                                                                                value="RECURRING"
                                                                                className="custom-card-radio"
                                                                            />
                                                                            <span className="custom-radio-circle"></span>
                                                                        </div>

                                                                        <div className="icon-wrapper">
                                                                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                                                                <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                                                                            </svg>
                                                                        </div>

                                                                        <div className="text-wrapper">
                                                                            <span className="card-title">Recurring</span>
                                                                            <span className="card-description">Continues sending automatically every X days until user activates.</span>
                                                                        </div>
                                                                    </label>
                                                                </Col>
                                                            </Row>

                                                            {values.reminder_schedule_type === "RECURRING" && (
                                                                <div className="mt-3 bg-white p-3 border rounded">
                                                                    <Row className="align-items-center">
                                                                        <Col xs="auto">
                                                                            <Label for="reminder_recurring_days" className="m-0">Interval Cadence (Days):</Label>
                                                                        </Col>
                                                                        <Col md="2">
                                                                            <Field
                                                                                name="reminder_recurring_days"
                                                                                component="input"
                                                                                type="number"
                                                                                className="form-control text-center"
                                                                                id="reminder_recurring_days"
                                                                                min="1"
                                                                                parse={value => isNaN(parseInt(value, 10)) ? "" : parseInt(value, 10)}
                                                                            />
                                                                        </Col>
                                                                    </Row>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </fieldset>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </form>
                )}
            </Form>
        </Container>
    );
};

export default withRouter(
    connect((state) => ({
        organization: state.organization,
        program: state.program,
        auth: state.auth,
    }))(ProgramEmailSettings)
);
